import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LayoutGrid, List, Loader2, Plus } from 'lucide-react';
import { projectApi } from '../mocks/projectApi';
import {
  PROJECT_STAGES,
  Project,
  ProjectStage,
  STAGE_STYLE,
} from '../types/project';
import { ErrorBanner, errorText } from '../components/ErrorBanner';
import { IntakePanel } from '../components/intake/IntakePanel';
import { UpcomingBilling } from '../components/billing/UpcomingBilling';
import { useAuth } from '../contexts/AuthContext';
import { useAlert } from '../contexts/AlertContext';

/**
 * 프로젝트 — 보드와 표를 오간다 (2026-09-20 구조 변경).
 *
 * 단계마다 메뉴를 두던 것을 여기 하나로 합쳤다. 단계는 보드의 칸이다.
 * 카드를 끌어 옮기면 단계가 바뀐다 — 단, **수행 칸은 수주 판정을 거쳐야**
 * 들어갈 수 있다. 실주한 건이 수행에 섞이면 안 된다.
 */
type View = 'board' | 'table';

function formatPrice(v: number | null): string {
  if (!v) return '—';
  if (v >= 100_000_000) return `${(v / 100_000_000).toFixed(1)}억`;
  return `${Math.round(v / 10_000).toLocaleString()}만`;
}

function formatDt(v: string): string {
  const d = new Date(v.replace(/\//g, '-'));
  if (isNaN(d.getTime())) return v;
  return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function daysLeft(v: string): number | null {
  const d = new Date(v.replace(/\//g, '-'));
  if (isNaN(d.getTime())) return null;
  const diff = d.getTime() - Date.now();
  return diff < 0 ? null : Math.ceil(diff / 86_400_000);
}

const LostPill: React.FC = () => (
  <span className="rounded-full px-2 py-0.5 text-xs font-semibold bg-red-50 text-red-600">실주</span>
);

/** 위 요약 줄. 보드가 아래를 다 채우지 못해 비어 보이는 것을 덜어 준다 */
const Stat: React.FC<{ label: string; value: string; note?: string; tone?: 'plain' | 'warn' }> = ({
  label, value, note, tone = 'plain',
}) => (
  <div className="bg-white border border-slate-200 rounded-lg px-3.5 py-2.5">
    <div className="text-xs text-slate-400">{label}</div>
    <div className={`mt-0.5 text-xl font-bold tabular-nums ${tone === 'warn' ? 'text-red-600' : 'text-slate-900'}`}>
      {value}
    </div>
    {note && <div className="text-xs text-slate-400">{note}</div>}
  </div>
);

/** 요약 줄에 쓸 값. 화면에 보이는 것만 센다 — 지어내지 않는다 */
function summarize(items: Project[]) {
  const open = items.filter((b) => b.stage !== '종료');
  const soon = open.filter((b) => {
    const d = daysLeft(b.dueAt);
    return d !== null && d <= 7;
  });
  // 제안 중인 금액 — 아직 판정 전이라 우리 것이 아니다
  const proposing = open
    .filter((b) => !b.outcome)
    .reduce((sum, b) => sum + (b.proposalPrice ?? 0), 0);
  /* 수주 · 수주율은 **이 앱에서 판정한 건**만 센다(`decidedAt`). 드라이브에서 들인 과거 건은 계약서가
     있는 것(=딴 것)뿐이라 실주가 0 이고 계약금액도 없다 — 섞으면 「수주율 100%」가 나왔다(2026-10-01).
     수주 카드는 그 수정이 빠져 금액은 판정한 건만 더하는데 건수는 과거까지 세었다(2026-10-23) */
  const judged = items.filter((b) => b.decidedAt && (b.outcome === '수주' || b.outcome === '실주'));
  const wonItems = judged.filter((b) => b.outcome === '수주');
  const wonAmount = wonItems.reduce((sum, b) => sum + (b.contractAmount ?? 0), 0);
  const decided = judged.length;
  return {
    open: open.length,
    soon: soon.length,
    proposing,
    wonAmount,
    wonCount: wonItems.length,
    rate: decided > 0 ? Math.round((wonItems.length / decided) * 100) : null,
    decided,
  };
}

export const ProjectListPage: React.FC = () => {
  const navigate = useNavigate();

  const [items, setItems] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<View>('board');
  const [dragId, setDragId] = useState<number | null>(null);
  const [overStage, setOverStage] = useState<ProjectStage | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);

  /* 목록을 못 불러오면 「프로젝트가 아직 없습니다」로 보이던 것을 막는다(#107) */
  const [loadError, setLoadError] = useState<string | null>(null);
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setItems(await projectApi.list());
    } catch (e) {
      setLoadError(`프로젝트를 불러오지 못했습니다 — ${errorText(e)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const drop = async (stage: ProjectStage) => {
    setOverStage(null);
    const id = dragId;
    setDragId(null);
    if (id === null) return;

    const b = items.find((x) => x.id === id);
    if (!b || b.stage === stage) return;

    // 수주 판정을 거치지 않은 건은 수행으로 못 간다 (설계 2026-09-20)
    if (stage === '수행' && b.outcome !== '수주') {
      setBlocked('수행으로 옮기려면 제안서 단계에서 수주 판정을 먼저 합니다.');
      return;
    }
    // 판정과 어긋나는 이동을 막는다(#110) — 서버도 같은 규칙으로 막는다
    if (stage === '종료' && !b.outcome && b.stage !== '수행') {
      setBlocked('판정 없이 종료로 옮기지 않습니다. 안 하기로 했으면 검토에서 「참여 안 함」, 떨어졌으면 수주 판정에서 「실주」를 고릅니다.');
      return;
    }
    if (b.outcome && stage !== '종료' && !(b.outcome === '수주' && stage === '수행')) {
      setBlocked(`${b.outcome}으로 판정한 건은 ${stage}(으)로 되돌리지 않습니다.`);
      return;
    }
    setBlocked(null);
    try {
      await projectApi.moveStage(id, stage);
    } catch (e) {
      setBlocked(`옮기지 못했습니다 — ${errorText(e)}`);
    }
    await load();
  };

  const open = (b: Project) => navigate(`/projects/${b.id}`);
  /** 「프로젝트 추가」 칸을 열었나 */
  const [adding, setAdding] = useState(false);
  const { user } = useAuth();
  const { showToast } = useAlert();
  const [q, setQ] = useState('');

  const sum = summarize(items);
  /* 검색 — 종료 칸에 과거 건이 80여 개라 이름으로 찾을 수 없었다(#111). 요약 줄은 전체 기준 그대로 */
  const needle = q.trim().toLowerCase();
  const shown = needle ? items.filter((b) => [b.name, b.clientName, b.pmName, b.bid?.bidNtceNo].join(' ').toLowerCase().includes(needle)) : items;

  return (
    <div className="h-full flex flex-col p-6 lg:p-8 max-w-[1400px]">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-1.5">
          프로젝트
        </h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="이름 · 고객사 · PM · 공고번호"
              className="w-56 pl-3 pr-7 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            {q && <button onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">×</button>}
          </div>
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            {([['board', '보드', LayoutGrid], ['table', '표', List]] as const).map(([k, label, Icon]) => (
              <button
                key={k}
                onClick={() => setView(k)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition ${
                  view === k ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>
          <button onClick={() => setAdding(true)} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700">
            <Plus size={13} /> 프로젝트 추가
          </button>
        </div>
      </div>

      <ErrorBanner message={loadError} onRetry={load} className="mb-3" />
      {/* 다가오는 청구 — 수행 중 프로젝트의 착수금 · 중도금 · 잔금 회차(작업 10) */}
      <UpcomingBilling />
      {adding && (
        <IntakePanel
          userName={user?.name ?? ''}
          onClose={() => setAdding(false)}
          onCreated={(id, warnings) => {
            // 프로젝트는 만들어졌다 — 드라이브 · 첨부 실패만 알린다
            if (warnings.length) showToast(`프로젝트는 만들었지만 ${warnings.length}건이 실패했습니다 — ${warnings[0]}`, 'warning');
            navigate(`/projects/${id}`);
          }}
        />
      )}

      {/* 요약 줄 — 보드가 아래를 다 못 채우니 위에서 받쳐 준다 */}
      {!loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
          <Stat label="진행 중" value={`${sum.open}건`} note={`전체 ${items.length}건`} />
          <Stat
            label="마감 임박"
            value={`${sum.soon}건`}
            note="7일 이내"
            tone={sum.soon > 0 ? 'warn' : 'plain'}
          />
          <Stat label="제안 중" value={formatPrice(sum.proposing)} note="판정 전" />
          <Stat label="수주" value={formatPrice(sum.wonAmount)} note={`${sum.wonCount}건 · 여기서 판정한 것만`} />
          <Stat
            label="수주율"
            value={sum.rate === null ? '—' : `${sum.rate}%`}
            note={sum.decided > 0 ? `여기서 판정 ${sum.decided}건` : '여기서 판정한 건 없음 · 과거 건 제외'}
          />
        </div>
      )}

      {blocked && (
        <p className="mb-3 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
          {blocked}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
          <Loader2 size={16} className="animate-spin" /> 불러오는 중...
        </div>
      ) : view === 'board' ? (
        <>
          <div className="flex-1 min-h-0 grid grid-rows-1 grid-flow-col auto-cols-[minmax(215px,1fr)] gap-2.5 overflow-x-auto pb-2 items-stretch">
            {PROJECT_STAGES.map((stage) => {
              const list = shown.filter((b) => b.stage === stage);
              const sum = list.reduce((s, b) => s + (b.proposalPrice ?? 0), 0);
              return (
                <div
                  key={stage}
                  onDragOver={(e) => { e.preventDefault(); setOverStage(stage); }}
                  onDragLeave={() => setOverStage((s) => (s === stage ? null : s))}
                  onDrop={(e) => { e.preventDefault(); drop(stage); }}
                  // 페이지 배경이 slate-100 이라 레인은 한 단계 낮춰야 보인다
                  className={`rounded-lg bg-slate-200/60 p-2 flex flex-col min-h-[180px] ${
                    overStage === stage ? 'outline outline-2 outline-dashed outline-indigo-400' : ''
                  }`}
                >
                  <div className="flex items-center justify-between px-1 pb-2">
                    <span className="text-xs font-semibold text-slate-700">
                      {stage} <span className="text-slate-400 font-normal tabular-nums">{list.length}</span>
                    </span>
                    {sum > 0 && <span className="text-xs text-slate-400 tabular-nums">{formatPrice(sum)}</span>}
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto">
                  {list.map((b, i) => {
                    const dday = daysLeft(b.dueAt);
                    return (
                      <div
                        key={b.id}
                        draggable
                        onDragStart={(e) => {
                          setDragId(b.id);
                          // Firefox 는 setData 없이는 드래그를 시작하지 않는다
                          e.dataTransfer.setData('text/plain', String(b.id));
                          e.dataTransfer.effectAllowed = 'move';
                        }}
                        onDragEnd={() => setDragId(null)}
                        onClick={() => open(b)}
                        className={`bg-white border border-slate-200 rounded-lg px-3 py-2.5 mb-1.5 cursor-pointer hover:border-slate-300 ${
                          dragId === b.id ? 'opacity-40' : ''
                        }`}
                      >
                        <div className="text-xs font-medium text-slate-900 leading-snug">{b.name}</div>
                        <div className="mt-1.5 flex items-center justify-between gap-2 text-xs text-slate-400">
                          <span className="truncate">{b.clientName}{b.pmName ? ` · ${b.pmName}` : ''}</span>
                          {b.outcome === '실주' ? (
                            <LostPill />
                          ) : dday === null && !b.outcome && ['검토', '재무계획', '제안서'].includes(b.stage) && b.dueAt && new Date(b.dueAt).getTime() < Date.now() ? (
                            /* 제안 전 단계인데 마감이 지났다 — 낼 수 없으면 검토에서 「참여 안 함」으로 뺀다 */
                            <span className="rounded-full px-2 py-0.5 text-xs font-semibold flex-shrink-0 bg-red-50 text-red-600">마감 지남</span>
                          ) : dday !== null ? (
                            <span
                              className={`rounded-full px-2 py-0.5 text-xs font-semibold flex-shrink-0 ${
                                dday <= 7 ? 'bg-red-50 text-red-600' : 'bg-slate-100 text-slate-500'
                              }`}
                            >
                              D-{dday}
                            </span>
                          ) : null}
                        </div>
                        <div className="mt-1 text-right text-xs font-semibold text-slate-700 tabular-nums">
                          {formatPrice(b.contractAmount ?? b.proposalPrice)}
                        </div>
                      </div>
                    );
                  })}
                  {list.length === 0 && (
                    <p className="px-1 pt-1 text-xs text-slate-400">없음</p>
                  )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="mt-2.5 flex-shrink-0 text-xs text-slate-400">
            카드를 끌어 다른 칸에 놓으면 단계가 바뀝니다. 칸 머리의 숫자는 건수, 오른쪽은 금액 합계입니다.
          </p>
        </>
      ) : shown.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 py-16 text-center">
          <p className="text-sm text-slate-500">{needle ? `「${q}」에 맞는 프로젝트가 없습니다.` : "프로젝트가 아직 없습니다."}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 overflow-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-xs text-slate-400">
                <th className="text-left font-medium px-4 py-2.5">프로젝트명</th>
                <th className="text-left font-medium px-2 py-2.5">발주기관</th>
                <th className="text-right font-medium px-2 py-2.5">제안가</th>
                <th className="text-left font-medium px-2 py-2.5">마감</th>
                <th className="text-left font-medium px-2 py-2.5">PM</th>
                <th className="text-left font-medium px-2 py-2.5">단계</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((b) => {
                const dday = daysLeft(b.dueAt);
                return (
                  <tr
                    key={b.id}
                    onClick={() => open(b)}
                    className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer align-top"
                  >
                    <td className="px-4 py-3 font-medium text-slate-900">{b.name}</td>
                    <td className="px-2 py-3 text-xs text-slate-600">{b.clientName}</td>
                    <td className="px-2 py-3 text-right font-medium text-slate-900 whitespace-nowrap">
                      {formatPrice(b.contractAmount ?? b.proposalPrice)}
                    </td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      {dday !== null && (
                        <span className="rounded-full px-2 py-1 text-xs font-semibold bg-slate-100 text-slate-600">
                          D-{dday}
                        </span>
                      )}
                      <div className="mt-1 text-xs text-slate-400">{formatDt(b.dueAt)}</div>
                    </td>
                    <td className="px-2 py-3 text-xs text-slate-600">{b.pmName || '—'}</td>
                    <td className="px-2 py-3 whitespace-nowrap">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${STAGE_STYLE[b.stage]}`}>
                        {b.stage}
                      </span>
                      {b.outcome === '실주' && <span className="ml-1"><LostPill /></span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};
